import { array, type InferType, lazy, object, string, type TestConfig } from 'yup';
import type { CompanionsFormValues } from '@/types';
import { getObjectKeys } from '@/utils/helpers';
import { getEmptyCompanions } from '@/components/tripBookingPage/formItems/companions/companionsFormItem.config.ts';

export const getCompanionsSchema = (minCompanions: number = 0) =>
  object({
    companions: lazy((companions, context) =>
      object({
        outward: getCompanionLazySchema(minCompanions),
        inward: getCompanionLazySchema(minCompanions, !context.parent.roundTripSameCompanions),
        assistiveDevices: array().of(string().defined()).default([]),
        modificationDevices: array().of(string().defined()).default([]),
        otherDevice: string().defined().default(''),
      })),
    roundTripSameCompanions: string().nonNullable().default('true').oneOf(['true', 'false']), // propriété interne mais essentielle au bon fonctionnement des accompagnateurs aller-retour.  Ne pas supprimer.
  }).default({
    companions: {
      outward: getEmptyCompanions(),
      inward: getEmptyCompanions(),
      assistiveDevices: [],
      modificationDevices: [],
      otherDevice: '',
    },
    roundTripSameCompanions: 'true',

  });

/**
 * Création du schéma de validation basé sur la disponibilité des types d'accompagnateurs.
 * Par exemple, si l'utilisateur peut avoir uniquement des enfants comme accompagnateurs,
 * la validation ne se fera uniquement que sur cette propriété.
 */
function getCompanionLazySchema(minCompanions: number = 0, required = true) {
  return lazy(function(companions: CompanionsFormValues = { travelling: null, children: null, parents: null }) {

    const travellingSchema = string().defined().nullable();
    const childrenSchema = string().defined().nullable();
    const parentsSchema = string().defined().nullable();

    if (required && companions.travelling !== null) {
      // `required()` renverse l'effet de 'optional` et `nullable`
      travellingSchema.required('generic.pleaseSelectAnOption');
    }
    if (required && companions.parents !== null) {
      // `required()` renverse l'effet de 'optional` et `nullable`
      parentsSchema.required('generic.pleaseSelectAnOption');
    }
    if (required && companions.children !== null) {
      // `required()` renverse l'effet de 'optional` et `nullable`
      childrenSchema.required('generic.pleaseSelectAnOption');
    }

    return object({
      travelling: travellingSchema
        .test(getMinCompanionsTest(companions, minCompanions))
        .notOneOf([''], 'generic.pleaseSelectAnOption'),
      children: childrenSchema
        .test(getMinCompanionsTest(companions, minCompanions))
        .notOneOf([''], 'generic.pleaseSelectAnOption'),
      parents: parentsSchema
        .test(getMinCompanionsTest(companions, minCompanions))
        .notOneOf([''], 'generic.pleaseSelectAnOption'),
    });
  });
}

/**
 * Validation de la sélection des accompagnateurs puis du nombre minimal exigé
 */
function getMinCompanionsTest(companions: CompanionsFormValues, minCompanions: number): TestConfig {
  return {
    name: 'minCompanionsValidator',
    message: 'pages.tripBooking.forms.items.companions.validations.minCompanions',
    params: { minCompanions, companions },
    test: () =>
      getObjectKeys(companions).some((type) => companions[type] === null) ||
      getSelectedCompanionsCount(companions) >= minCompanions,
  };
}

export function getSelectedCompanionsCount(companions: CompanionsFormValues): number {
  return getObjectKeys(companions).reduce((acc, type) => (acc += parseInt(companions[type] || '0', 10)), 0);
}

export type CompanionSchema = InferType<ReturnType<typeof getCompanionsSchema>>;
